import React, { useState, useEffect } from 'react';
import { X, MapPin, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Dropdown from '../../components/Dropdown';
import { getStateOptions, getCityOptions, getCitiesForState } from '../../utils/locationData';

const formatDateTimeLocal = (dateVal) => {
  if (!dateVal) return '';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const AddDelivery = ({ onClose, onSuccess, delivery }) => {
  const [formData, setFormData] = useState({
    leadId: '',
    title: '',
    deliveryBoyId: '',
    status: 'Pending',
    scheduledDate: '',
    details: '',
    orderNo: '',
    orderDate: '',
    amount: '',
    partyName: '',
    partyPhone: '',
    partyEmail: '',
    partyAddress: '',
    partyCity: '',
    partyState: '',
    partyPincode: '',
    latitude: '',
    longitude: ''
  });
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusOptions, setStatusOptions] = useState([
    { value: 'Pending', label: 'Pending' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Cancelled', label: 'Cancelled' }
  ]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isOtherState, setIsOtherState] = useState(false);
  const [isOtherCity, setIsOtherCity] = useState(false);
  const [otherState, setOtherState] = useState('');
  const [otherCity, setOtherCity] = useState('');

  useEffect(() => {
    if (delivery) {
      let latVal = delivery.latitude !== null && delivery.latitude !== undefined ? delivery.latitude : '';
      let lngVal = delivery.longitude !== null && delivery.longitude !== undefined ? delivery.longitude : '';
      let rawAddr = delivery.partyAddress || delivery.lead?.address || '';
      
      if ((!latVal || !lngVal) && rawAddr) {
        const gpsMatch = rawAddr.match(/\[GPS:\s*Lat:\s*([\d.-]+),\s*Lon:\s*([\d.-]+)\]/i);
        if (gpsMatch) {
          latVal = gpsMatch[1];
          lngVal = gpsMatch[2];
        }
      }
      const cleanAddress = rawAddr ? rawAddr.replace(/\s*\[GPS:\s*Lat:\s*[\d.-]+,\s*Lon:\s*[\d.-]+\]/gi, '').trim() : '';

      setIsOtherState(false);
      setIsOtherCity(false);
      setOtherState('');
      setOtherCity('');

      setFormData({
        leadId: delivery.leadId || '',
        title: delivery.title || '',
        deliveryBoyId: delivery.deliveryBoyId || '',
        status: delivery.status || 'Pending',
        scheduledDate: delivery.scheduledDate ? formatDateTimeLocal(delivery.scheduledDate) : '',
        details: delivery.details || '',
        orderNo: delivery.orderNo || '',
        orderDate: delivery.orderDate ? new Date(delivery.orderDate).toISOString().split('T')[0] : '',
        amount: delivery.amount !== undefined && delivery.amount !== null ? delivery.amount : '',
        partyName: delivery.partyName || delivery.lead?.companyName || '',
        partyPhone: delivery.partyPhone || delivery.lead?.phone || '',
        partyEmail: delivery.partyEmail || delivery.lead?.email || '',
        partyAddress: cleanAddress,
        partyCity: delivery.partyCity || delivery.lead?.city || '',
        partyState: delivery.partyState || delivery.lead?.state || '',
        partyPincode: delivery.partyPincode || delivery.lead?.pincode || '',
        latitude: latVal,
        longitude: lngVal
      });
    }
  }, [delivery]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadsRes, usersRes, statusRes] = await Promise.all([
          api.get('/leads?limit=1000'),
          api.get('/auth/users?page=1&limit=1000').catch(() => ({ data: [] })),
          api.get('/masters/delivery-statuses').catch(() => ({ data: [] }))
        ]);
        
        if (leadsRes.data && leadsRes.data.data) {
          setLeads(leadsRes.data.data);
        } else if (Array.isArray(leadsRes.data)) {
          setLeads(leadsRes.data);
        }
        
        if (usersRes.data && usersRes.data.data) {
          const allUsers = usersRes.data.data;
          setUsers(allUsers.filter(u => {
            const roleName = (u.role?.name || u.role || '').toString().toLowerCase();
            return roleName === 'delivery boy';
          }));
        } else if (Array.isArray(usersRes.data)) {
          const allUsers = usersRes.data;
          setUsers(allUsers.filter(u => {
            const roleName = (u.role?.name || u.role || '').toString().toLowerCase();
            return (roleName === 'delivery boy' || roleName.includes('delivery')) || true;
          }));
        }

        const rawStatuses = statusRes.data?.data || (Array.isArray(statusRes.data) ? statusRes.data : []);
        const activeStatuses = rawStatuses
          .filter(s => s.status === 'Active')
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        if (activeStatuses.length > 0) {
          setStatusOptions(activeStatuses.map(s => ({ value: s.name, label: s.name })));
        }
      } catch (err) {
        console.error("Failed to load options", err);
        toast.error("Failed to load leads or users");
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'leadId' && value) {
        const selectedLead = leads.find(l => String(l.id) === String(value));
        if (selectedLead) {
          updated.partyName = selectedLead.companyName || selectedLead.contactPerson || '';
          updated.partyPhone = selectedLead.phone || '';
          updated.partyEmail = selectedLead.email || '';
          updated.partyAddress = selectedLead.address || '';
          updated.partyCity = selectedLead.city || '';
          updated.partyState = selectedLead.state || '';
          updated.partyPincode = selectedLead.pincode || '';
          setIsOtherState(false);
          setIsOtherCity(false);
          setOtherState('');
          setOtherCity('');
          if (selectedLead.latitude && selectedLead.longitude) {
            updated.latitude = selectedLead.latitude;
            updated.longitude = selectedLead.longitude;
          }
        }
      }
      return updated;
    });
  };

  const handleStateChange = (val) => {
    if (val === 'Other') {
      setIsOtherState(true);
      setIsOtherCity(true);
      setFormData(prev => ({ ...prev, partyState: 'Other', partyCity: 'Other' }));
    } else {
      setIsOtherState(false);
      const availableCities = getCitiesForState(val);
      const currentCity = formData.partyCity;
      const nextCity = (availableCities.includes(currentCity) && currentCity !== 'Other')
        ? currentCity
        : (availableCities[0] || '');
      setFormData(prev => ({ ...prev, partyState: val, partyCity: nextCity }));
      setIsOtherCity(false);
    }
    if (errors.partyState) setErrors(prev => ({ ...prev, partyState: null }));
    if (errors.partyCity) setErrors(prev => ({ ...prev, partyCity: null }));
  };

  const handleCityChange = (val) => {
    if (val === 'Other') {
      setIsOtherCity(true);
      setFormData(prev => ({ ...prev, partyCity: 'Other' }));
    } else {
      setIsOtherCity(false);
      setFormData(prev => ({ ...prev, partyCity: val }));
    }
    if (errors.partyCity) setErrors(prev => ({ ...prev, partyCity: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const finalState = (formData.partyState === 'Other' || isOtherState) ? otherState.trim() : formData.partyState?.trim();
    const finalCity = (formData.partyCity === 'Other' || isOtherCity) ? otherCity.trim() : formData.partyCity?.trim();
    
    const newErrors = {};
    if (!formData.partyName?.trim()) newErrors.partyName = "Party / Customer Name is required";
    if (!formData.partyPhone?.trim()) newErrors.partyPhone = "Phone Number is required";
    else if (formData.partyPhone.trim().length !== 10) newErrors.partyPhone = "Phone number must be exactly 10 digits";
    
    if (!formData.title?.trim()) newErrors.title = "Delivery Title is required";
    
    if (!formData.partyEmail?.trim()) newErrors.partyEmail = "Email Address is required";
    else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.partyEmail.trim())) newErrors.partyEmail = "Please provide a valid Email Address";

    if (!formData.partyAddress?.trim()) newErrors.partyAddress = "Delivery Address is required";
    if (!finalCity) newErrors.partyCity = "City is required";
    if (!finalState) newErrors.partyState = "State is required";
    if (!formData.partyPincode?.trim()) newErrors.partyPincode = "Pincode is required";
    if (!formData.deliveryBoyId) newErrors.deliveryBoyId = "Delivery Boy is required";
    if (!formData.scheduledDate) newErrors.scheduledDate = "Scheduled Date & Time is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
      return;
    }
    setErrors({});

    
    try {
      setLoading(true);
      const payload = {
        ...formData,
        partyState: finalState,
        partyCity: finalCity,
        leadId: formData.leadId ? Number(formData.leadId) : null,
        deliveryBoyId: formData.deliveryBoyId ? Number(formData.deliveryBoyId) : null,
        latitude: formData.latitude !== '' && formData.latitude !== null ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude !== '' && formData.longitude !== null ? parseFloat(formData.longitude) : null
      };

      if (delivery) {
        await api.put(`/deliveries/${delivery.id}`, payload);
        toast.success("Delivery updated");
      } else {
        await api.post('/deliveries', payload);
        toast.success("Delivery created");
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '850px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
        <div className="modal-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-text-main)' }}>{delivery ? 'Edit Delivery' : 'Add Delivery'}</h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }} noValidate>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>

            {/* 1. Party / Customer Details Section */}
            <div style={{ gridColumn: 'span 3', marginBottom: '0.25rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>1. Party / Customer Details</h3>
            </div>

            {/* Row 1: Select Lead, Party / Customer Name, Phone Number */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Select Lead (Optional)</label>
              <Dropdown 
                searchable={true}
                placeholder="Select a Lead"
                value={formData.leadId}
                onChange={(val) => handleDropdownChange('leadId', val)}
                options={leads.map(lead => ({ value: lead.id, label: `${lead.companyName} (${lead.contactPerson})` }))}
                style={{ pointerEvents: delivery ? 'none' : 'auto', opacity: delivery ? 0.7 : 1 }}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Party / Customer Name <span style={{color: "var(--color-danger)"}}>*</span></label>
              <input type="text" name="partyName" value={formData.partyName} onChange={handleChange} className="input-field" placeholder="Party Name" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
              {errors.partyName && <div className="text-danger text-xs mt-1">{errors.partyName}</div>}
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Phone Number <span style={{color: "var(--color-danger)"}}>*</span></label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '1px', top: '1px', bottom: '1px', display: 'flex', alignItems: 'center', padding: '0 0.75rem', backgroundColor: '#f1f5f9', borderRight: '1px solid var(--color-border)', borderTopLeftRadius: '11px', borderBottomLeftRadius: '11px', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.9rem', pointerEvents: 'none' }}>+91</span>
                <input type="text" name="partyPhone" required value={formData.partyPhone} onChange={handleChange} className="input-field" placeholder="98765 43210" style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 3.5rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
              </div>
              {errors.partyPhone && <div className="text-danger text-xs mt-1">{errors.partyPhone}</div>}
            </div>

            {/* Row 2: Email Address, Delivery Title, Delivery Address */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Email Address <span style={{color: "var(--color-danger)"}}>*</span></label>
              <input type="email" name="partyEmail" required value={formData.partyEmail} onChange={handleChange} className="input-field" placeholder="Email Address" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
              {errors.partyEmail && <div className="text-danger text-xs mt-1">{errors.partyEmail}</div>}
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Delivery Title / Item Name <span style={{color: "var(--color-danger)"}}>*</span></label>
              <input type="text" name="title" required value={formData.title} onChange={handleChange} className="input-field" placeholder="E.g. Sample Product Delivery" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
              {errors.title && <div className="text-danger text-xs mt-1">{errors.title}</div>}
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Delivery Address <span style={{color: "var(--color-danger)"}}>*</span></label>
              <input type="text" name="partyAddress" required value={formData.partyAddress} onChange={handleChange} className="input-field" placeholder="Street Address, Building, Area" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
              {errors.partyAddress && <div className="text-danger text-xs mt-1">{errors.partyAddress}</div>}
            </div>

            {/* Row 3: State, City, Pincode */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>State <span style={{color: "var(--color-danger)"}}>*</span></label>
              <Dropdown
                placeholder="Select State..."
                searchable={true}
                value={formData.partyState}
                onChange={handleStateChange}
                options={getStateOptions(formData.partyState)}
              />
              {errors.partyState && <div className="text-danger text-xs mt-1">{errors.partyState}</div>}
            </div>

            {isOtherState && (
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Specify State <span style={{color: "var(--color-danger)"}}>*</span></label>
                <input
                  type="text"
                  name="otherState"
                  className="input-field"
                  placeholder="Enter State Name"
                  value={otherState}
                  onChange={e => {
                    setOtherState(e.target.value);
                    if (errors.partyState) setErrors(prev => ({ ...prev, partyState: null }));
                  }}
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }}
                />
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>City <span style={{color: "var(--color-danger)"}}>*</span></label>
              <Dropdown
                placeholder="Select City..."
                searchable={true}
                value={formData.partyCity}
                onChange={handleCityChange}
                options={getCityOptions(formData.partyState, formData.partyCity)}
              />
              {errors.partyCity && <div className="text-danger text-xs mt-1">{errors.partyCity}</div>}
            </div>

            {isOtherCity && (
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Specify City <span style={{color: "var(--color-danger)"}}>*</span></label>
                <input
                  type="text"
                  name="otherCity"
                  className="input-field"
                  placeholder="Enter City Name"
                  value={otherCity}
                  onChange={e => {
                    setOtherCity(e.target.value);
                    if (errors.partyCity) setErrors(prev => ({ ...prev, partyCity: null }));
                  }}
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }}
                />
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Pincode <span style={{color: "var(--color-danger)"}}>*</span></label>
              <input type="text" name="partyPincode" required value={formData.partyPincode} onChange={handleChange} className="input-field" placeholder="Pincode" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
              {errors.partyPincode && <div className="text-danger text-xs mt-1">{errors.partyPincode}</div>}
            </div>

            
            {/* View GPS Location on Map if available */}
            {(formData.latitude && formData.longitude) && (
              <div style={{ gridColumn: 'span 3', marginTop: '0.25rem', padding: '0.85rem 1rem', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <MapPin size={16} color="#0284c7" /> Location Captured
                </span>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${formData.latitude},${formData.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                  style={{ textDecoration: 'none', padding: '0.3rem 0.75rem', fontSize: '0.78rem', backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                >
                  🗺️ View in Google Maps
                </a>
              </div>
            )}
            
            {/* 2. Order & Payment Info Section */}
            <div style={{ gridColumn: 'span 3', marginTop: '0.5rem', marginBottom: '0.25rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>2. Order & Payment Info</h3>
            </div>

            {/* Row 4: Order No, Order Date, Payment Amount (₹) */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Order No</label>
              <input type="text" name="orderNo" value={formData.orderNo} onChange={handleChange} className="input-field" placeholder="E.g. ORD-1002" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
              {errors.orderNo && <div className="text-danger text-xs mt-1">{errors.orderNo}</div>}
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Order Date</label>
              <input type="date" name="orderDate" value={formData.orderDate} onChange={handleChange} className="input-field" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
              {errors.orderDate && <div className="text-danger text-xs mt-1">{errors.orderDate}</div>}
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Payment Amount (₹)</label>
              <input type="number" step="0.01" name="amount" value={formData.amount} onChange={handleChange} className="input-field" placeholder="E.g. 1500.00" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            {/* 3. Assignment & Status Section */}
            <div style={{ gridColumn: 'span 3', marginTop: '0.5rem', marginBottom: '0.25rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>3. Assignment & Status</h3>
            </div>

            {/* Row 5: Assign Delivery Boy, Scheduled Date, Status */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Assign Delivery Boy <span style={{color: "var(--color-danger)"}}>*</span></label>
              <Dropdown 
                searchable={true}
                placeholder="Select Delivery Boy"
                value={formData.deliveryBoyId}
                onChange={(val) => handleDropdownChange('deliveryBoyId', val)}
                options={users.map(user => ({ value: user.id, label: `${user.name} (${user.role?.name || user.role || 'User'})` }))}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Scheduled Date & Time <span style={{color: "var(--color-danger)"}}>*</span></label>
              <input type="datetime-local" name="scheduledDate" required value={formData.scheduledDate} onChange={handleChange} className="input-field" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
              {errors.scheduledDate && <div className="text-danger text-xs mt-1">{errors.scheduledDate}</div>}
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Status</label>
              <Dropdown 
                placeholder="Select Status"
                value={formData.status}
                onChange={(val) => handleDropdownChange('status', val)}
                options={statusOptions}
              />
            </div>

            {/* Row 6: Details / Notes */}
            <div className="form-group" style={{ gridColumn: 'span 3' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Details / Notes</label>
              <textarea name="details" value={formData.details} onChange={handleChange} className="input-field" rows="3" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px', resize: 'vertical' }} placeholder="Any instructions for delivery..."></textarea>
            </div>
          </div>

          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.25rem' }}>
            <button type="button" onClick={onClose} className="btn" style={{ padding: '0.5rem 1.5rem', border: '1px solid var(--color-border)', borderRadius: '12px', background: 'white', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
            <button type="submit" disabled={loading} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.5rem', border: 'none', borderRadius: '12px', background: 'var(--color-primary)', color: 'white', cursor: 'pointer', fontWeight: '500' }}>
              {loading && <Loader2 size={16} className="spinner-icon" />}
              {loading ? 'Saving...' : 'Save Delivery'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDelivery;
